import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Modal,
  Platform,
} from "react-native";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { observer, Observer } from "mobx-react";
import GlobalStyles from "../../../Resources/Styles";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { Entypo } from "@expo/vector-icons";
import { Feather, Ionicons } from "@expo/vector-icons";
import { useScreenDimensions, useDevice } from "../../_layout";
import CustomAlert from "../../Components/CustomAlert";
import { useTranslation } from "react-i18next";
import AddWorkoutView from "../../Workout/AddWorkoutView";
import IconEmptyView from "../../Components/IconEmptyView";
import SearchBar from "../../Components/SearchBar";
import CTAButton from "../../Components/CTAButton";
import TutorialLinkSection from "../../Components/TutorialLinkSection";
import { getWorkoutRowPreviewData } from "./workoutRowPreviewUtils";
import FloatingMenu from "../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import WorkoutCard from "../../Workout/shared/WorkoutCard";
import { ExerciseDndProvider } from "../../Workout/shared/exerciseDrag/ExerciseDrag";
import { ExerciseDragProvider } from "../../contexts/ExerciseDragContext";
import { useToast } from "../../contexts/ToastContext";
import { useDrag, useDrop } from "react-dnd";
import {
  createLibraryWorkoutCardAdapter,
  pasteExercisesIntoLibraryWorkout,
  persistLibraryWorkoutChange,
} from "./workoutLibraryEditorUtils";
import {
  buildOrderedIds,
  commitWorkoutLibraryOrder,
  moveItem,
} from "./workoutLibraryReorderUtils";
import { BlockPlanificationConstants } from "../../../Resources/Constants";

const INLINE_WORKOUT_NAME_MAX_LENGTH = 45;
const COACH_LIBRARY_WORKOUT_DND_TYPE = "COACH_LIBRARY_WORKOUT";

const getDOMElement = (ref) => {
  if (!ref) return null;
  if (ref.getBoundingClientRect) return ref;
  if (ref._component) return ref._component;
  if (typeof ref.getNode === "function") return ref.getNode();
  return ref;
};

const hasSameOrder = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
};

const useInlineWorkoutNameEditor = (workout) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(workout?.name ?? "");

  const inputRef = useRef(null);
  const nameDraftRef = useRef(workout?.name ?? "");
  const nameOriginalRef = useRef(workout?.name ?? "");
  const skipNextCommitRef = useRef(false);
  const skipNextBlurCommitRef = useRef(false);
  const pendingBlurTimerRef = useRef(null);

  const clearPendingBlurTimer = () => {
    if (pendingBlurTimerRef.current) {
      clearTimeout(pendingBlurTimerRef.current);
      pendingBlurTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (isEditingName) return;
    const nextName = workout?.name ?? "";
    nameDraftRef.current = nextName;
    nameOriginalRef.current = nextName;
    setNameDraft(nextName);
  }, [workout?.name, isEditingName]);

  useEffect(
    () => () => {
      if (pendingBlurTimerRef.current) {
        clearTimeout(pendingBlurTimerRef.current);
        pendingBlurTimerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!isEditingName) return;
    const id = setTimeout(() => {
      inputRef.current?.focus?.();
    }, 0);
    return () => clearTimeout(id);
  }, [isEditingName]);

  const startEditingName = () => {
    const currentName = workout?.name ?? "";
    nameOriginalRef.current = currentName;
    nameDraftRef.current = currentName;
    setNameDraft(currentName);
    skipNextCommitRef.current = false;
    skipNextBlurCommitRef.current = false;
    setIsEditingName(true);
  };

  const commitName = () => {
    skipNextBlurCommitRef.current = true;
    clearPendingBlurTimer();

    if (skipNextCommitRef.current) {
      skipNextCommitRef.current = false;
      setIsEditingName(false);
      return;
    }

    const trimmedDraft =
      typeof nameDraftRef.current === "string"
        ? nameDraftRef.current.trim()
        : "";
    const fallbackName =
      typeof nameOriginalRef.current === "string"
        ? nameOriginalRef.current
        : "";
    const nextName = trimmedDraft || fallbackName;
    if (nextName && nextName !== workout?.name) {
      workout?.setProperty?.("name", nextName);
    }
    setIsEditingName(false);
  };

  const cancelEditingName = () => {
    skipNextCommitRef.current = true;
    skipNextBlurCommitRef.current = true;
    clearPendingBlurTimer();
    const original =
      typeof nameOriginalRef.current === "string"
        ? nameOriginalRef.current
        : "";
    nameDraftRef.current = original;
    setNameDraft(original);
    setIsEditingName(false);
    inputRef.current?.blur?.();
  };

  const prepareCancelClick = () => {
    skipNextCommitRef.current = true;
    skipNextBlurCommitRef.current = true;
    clearPendingBlurTimer();
  };

  const prepareSaveClick = () => {
    skipNextCommitRef.current = false;
  };

  const handleNameChange = (value) => {
    nameDraftRef.current = value;
    setNameDraft(value);
  };

  const handleNameBlur = () => {
    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      return;
    }
    clearPendingBlurTimer();
    pendingBlurTimerRef.current = setTimeout(() => {
      pendingBlurTimerRef.current = null;
      commitName();
    }, 120);
  };

  const handleNameKey = (e) => {
    const key = e?.nativeEvent?.key ?? e?.key;
    if (key === "Enter") {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      commitName();
      return;
    }
    if (key === "Escape") {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      cancelEditingName();
    }
  };

  return {
    isEditingName,
    nameDraft,
    inputRef,
    startEditingName,
    commitName,
    cancelEditingName,
    prepareCancelClick,
    prepareSaveClick,
    handleNameChange,
    handleNameBlur,
    handleNameKey,
  };
};

const WorkoutLibrary = observer(({ coach }) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [showingAddNewWorkout, setShowingAddNewWorkout] = useState(false);
  const [pressSuppressedWorkoutId, setPressSuppressedWorkoutId] =
    useState(null);
  const [libraryFloatingPortalRoot, setLibraryFloatingPortalRoot] =
    useState(null);
  const pressSuppressTimeoutRef = useRef(null);
  const dragStartIdsRef = useRef([]);
  const draggedWorkoutIdRef = useRef(null);
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  const selectedWorkout = coach?.selectedWorkout ?? null;
  const showSetPriority = coach?.showSetPriority ?? false;
  const baseLibraryCardWidth = isMobile
    ? BlockPlanificationConstants.MOBILE_COLUMN_WIDTH
    : BlockPlanificationConstants.COLUMN_WIDTH;
  const libraryWorkoutCardWidth = showSetPriority
    ? baseLibraryCardWidth + (isMobile ? 28 : 32)
    : baseLibraryCardWidth;
  const handleLibraryFloatingPortalHostRef = useCallback((node) => {
    setLibraryFloatingPortalRoot((current) =>
      current === node ? current : node || null,
    );
  }, []);

  useEffect(
    () => () => {
      if (pressSuppressTimeoutRef.current) {
        clearTimeout(pressSuppressTimeoutRef.current);
        pressSuppressTimeoutRef.current = null;
      }
    },
    [],
  );

  const schedulePressSuppressionClear = useCallback(() => {
    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
    }

    pressSuppressTimeoutRef.current = setTimeout(() => {
      setPressSuppressedWorkoutId(null);
      pressSuppressTimeoutRef.current = null;
    }, 220);
  }, []);

  const resetDragState = useCallback(() => {
    dragStartIdsRef.current = [];
    draggedWorkoutIdRef.current = null;
  }, []);

  const handleWorkoutDragStart = useCallback((workoutId, orderedIds) => {
    if (!Array.isArray(orderedIds) || orderedIds.length < 2) return;

    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
      pressSuppressTimeoutRef.current = null;
    }

    const normalizedWorkoutId = String(workoutId);
    dragStartIdsRef.current = [...orderedIds];
    draggedWorkoutIdRef.current = normalizedWorkoutId;
  }, []);

  const handleWorkoutDrop = useCallback(
    ({ draggedId, dropIndex }) => {
      const startIds = dragStartIdsRef.current;
      if (!Array.isArray(startIds) || startIds.length === 0) return;

      const sourceIndex = startIds.findIndex((id) => id === draggedId);
      if (sourceIndex < 0) return;
      if (sourceIndex === dropIndex) return;

      const finalIds = moveItem(startIds, sourceIndex, dropIndex);
      if (hasSameOrder(startIds, finalIds)) return;

      commitWorkoutLibraryOrder({ coach, orderedIds: finalIds });
    },
    [coach],
  );

  const handleWorkoutDragEnd = useCallback(() => {
    const draggedWorkoutId = draggedWorkoutIdRef.current;

    resetDragState();
    if (draggedWorkoutId) {
      setPressSuppressedWorkoutId(draggedWorkoutId);
    }
    schedulePressSuppressionClear();
  }, [resetDragState, schedulePressSuppressionClear]);

  const closeWorkoutEditorModal = useCallback(() => {
    coach?.setSelectedWorkout?.(null);
    coach?.setProperty?.("showingInfoView", false);
  }, [coach]);

  const handleWorkoutEditorModalRequestClose = useCallback(() => {
    if (Platform.OS === "web") return;
    closeWorkoutEditorModal();
  }, [closeWorkoutEditorModal]);

  const deleteLibraryWorkout = useCallback(
    (workout) => {
      if (!workout) return;

      if (workout.coach) {
        workout.coach.updateCoachDeletionWorkouts(workout.id);
        workout.coach.startOrResetBatchTimer();
      }

      const currentLibrary = Array.isArray(coach?.workoutLibrary)
        ? coach.workoutLibrary
        : [];
      const nextLibrary = currentLibrary.filter((w) => w.id !== workout.id);
      coach?.setProperty?.("workoutLibrary", nextLibrary);

      if (coach?.selectedWorkout?.id === workout.id) {
        closeWorkoutEditorModal();
      }
    },
    [coach, closeWorkoutEditorModal],
  );

  const handleCopyExercise = useCallback(
    (exercise) => {
      if (!exercise) return;
      coach?.copyExercisesFromExercise?.(exercise);
    },
    [coach],
  );

  const handlePasteExercise = useCallback(
    (workout) => {
      pasteExercisesIntoLibraryWorkout({
        workout,
        coach,
        t,
        showToast,
      });
    },
    [coach, t, showToast],
  );

  const handleMoveExercise = useCallback(
    (workoutId, fromIndex, toIndex, exerciseId) => {
      if (!selectedWorkout) return null;
      if (String(selectedWorkout.id) !== String(workoutId)) return null;
      if (fromIndex === toIndex) return null;

      selectedWorkout?.reorderExercise?.(fromIndex, toIndex);
      persistLibraryWorkoutChange(coach, selectedWorkout);
      return exerciseId ?? null;
    },
    [coach, selectedWorkout],
  );

  const libraryBlockAdapter = useMemo(() => {
    if (!selectedWorkout) return null;
    return createLibraryWorkoutCardAdapter({
      workout: selectedWorkout,
      coach,
    });
  }, [selectedWorkout, coach]);

  const WorkoutRow = observer(
    ({
      workout,
      dragIndex,
      orderedIds,
      canReorder,
      suppressPress = false,
      isMobile: isMobileProp = false,
    }) => {
      const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
      const deleteAlertTitle = "deleteWorkoutAlertTitle-string";
      const deleteAlertText = "deleteWorkoutAlertText-string";
      const workoutId = String(workout?.id ?? "");
      const {
        isEditingName: isEditingWorkoutName,
        nameDraft: workoutNameDraft,
        inputRef: workoutNameInputRef,
        startEditingName: startEditingWorkoutName,
        commitName: commitWorkoutName,
        cancelEditingName: cancelWorkoutNameEdit,
        prepareCancelClick: prepareWorkoutNameCancelClick,
        prepareSaveClick: prepareWorkoutNameSaveClick,
        handleNameChange: handleWorkoutNameChange,
        handleNameBlur: handleWorkoutNameBlur,
        handleNameKey: handleWorkoutNameKey,
      } = useInlineWorkoutNameEditor(workout);

      const action = () => {
        coach?.setSelectedWorkout?.(workout);
      };

      const [isHovered, setIsHovered] = useState(false);
      const [isMenuHovered, setIsMenuHovered] = useState(false);

      const workoutMenuItems = [
        {
          key: "changeName",
          label: t("rename-string"),
        },
        {
          key: "delete",
          label: t("delete-string"),
          isDanger: true,
        },
      ];

      const [{ isOver }, drop] = useDrop({
        accept: COACH_LIBRARY_WORKOUT_DND_TYPE,
        canDrop: (item) => {
          if (!canReorder) return false;
          if (!item || item.type !== COACH_LIBRARY_WORKOUT_DND_TYPE)
            return false;
          return item.id !== workoutId;
        },
        drop: (item, monitor) => {
          if (!canReorder) return;
          if (!item || item.type !== COACH_LIBRARY_WORKOUT_DND_TYPE) return;
          if (monitor.didDrop()) return;
          return { dropIndex: dragIndex };
        },
        collect: (monitor) => ({
          isOver: monitor.isOver({ shallow: true }),
        }),
      });

      const [{ isDragging }, drag] = useDrag({
        type: COACH_LIBRARY_WORKOUT_DND_TYPE,
        canDrag: () => canReorder && !isEditingWorkoutName,
        item: () => {
          const startIndex = Array.isArray(orderedIds)
            ? orderedIds.findIndex((id) => id === workoutId)
            : dragIndex;

          handleWorkoutDragStart(workoutId, orderedIds);

          return {
            type: COACH_LIBRARY_WORKOUT_DND_TYPE,
            id: workoutId,
            index: startIndex >= 0 ? startIndex : dragIndex,
          };
        },
        end: (item, monitor) => {
          const dropResult = monitor.getDropResult();
          if (
            canReorder &&
            dropResult &&
            typeof dropResult.dropIndex === "number" &&
            item?.id
          ) {
            handleWorkoutDrop({
              draggedId: item.id,
              dropIndex: dropResult.dropIndex,
            });
          }
          handleWorkoutDragEnd();
        },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      });

      const dragDropRef = useCallback(
        (node) => {
          const domNode = getDOMElement(node);
          if (!domNode) {
            drag(null);
            drop(null);
            return;
          }
          drag(drop(domNode));
        },
        [drag, drop],
      );

      const isDropZoneActive = isOver && !isDragging;

      const columnCount =
        screenSize === "small" ? 1 : screenSize === "medium" ? 2 : 3;
      const columnGap = screenSize === "small" ? 8 : 12;
      const totalGap = (columnCount - 1) * columnGap;
      const { rows, remainingCount } = getWorkoutRowPreviewData({
        workout,
        t,
        language: i18n.language,
        previewLimit: 3,
      });

      return (
        <View
          style={{
            width: `calc((100% - ${totalGap}px) / ${columnCount})`,
          }}
        >
          <TouchableOpacity
            ref={dragDropRef}
            onPress={() => {
              if (isEditingWorkoutName || suppressPress || isDragging) return;
              action();
            }}
            style={[
              styles.workoutRowTouchable,
              isDragging ? styles.workoutRowTouchableDragging : null,
            ]}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            activeOpacity={0.92}
          >
            <View
              style={[
                styles.workoutRowCard,
                isHovered && !isMobile ? styles.workoutRowCardHovered : null,
                isDropZoneActive ? styles.workoutRowCardDropZoneActive : null,
                { transition: "border-color 200ms ease-in-out" },
              ]}
            >
              <View style={styles.workoutRowHeader}>
                {isEditingWorkoutName ? (
                  <TextInput
                    ref={workoutNameInputRef}
                    style={[
                      styles.workoutRowTitleInput,
                      isMobileProp && styles.workoutRowTitleInputMobile,
                    ]}
                    value={workoutNameDraft}
                    onChangeText={handleWorkoutNameChange}
                    onKeyDown={handleWorkoutNameKey}
                    onKeyPress={handleWorkoutNameKey}
                    onSubmitEditing={commitWorkoutName}
                    onBlur={handleWorkoutNameBlur}
                    blurOnSubmit={true}
                    returnKeyType="done"
                    numberOfLines={1}
                    maxLength={INLINE_WORKOUT_NAME_MAX_LENGTH}
                  />
                ) : (
                  <Text numberOfLines={1} style={styles.workoutRowTitle}>
                    {workout.name}
                  </Text>
                )}

                {isEditingWorkoutName ? (
                  <View style={styles.workoutInlineRenameActions}>
                    <HoverableTouchableOpacity
                      style={styles.workoutInlineRenameButton}
                      hoverStyle={styles.workoutInlineRenameButtonHover}
                      onPressIn={prepareWorkoutNameCancelClick}
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        cancelWorkoutNameEdit();
                      }}
                      title={t("cancel-string")}
                    >
                      <Feather name="x" size={14} color={Colors.grayColor} />
                    </HoverableTouchableOpacity>
                    <HoverableTouchableOpacity
                      style={styles.workoutInlineRenameButton}
                      hoverStyle={styles.workoutInlineRenameButtonHover}
                      onPressIn={prepareWorkoutNameSaveClick}
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        commitWorkoutName();
                      }}
                      title={t("save-string")}
                    >
                      <Ionicons
                        name="checkmark"
                        size={14}
                        color={Colors.bubbleColor}
                      />
                    </HoverableTouchableOpacity>
                  </View>
                ) : (
                  <FloatingMenu
                    placement="bottom-end"
                    width={180}
                    items={workoutMenuItems}
                    trigger={
                      <View
                        style={[
                          styles.workoutRowMenuAnchor,
                          {
                            opacity: isMenuHovered ? 1 : 0.76,
                            transition: "opacity 150ms ease",
                          },
                        ]}
                        onMouseEnter={() => !isMobile && setIsMenuHovered(true)}
                        onMouseLeave={() => setIsMenuHovered(false)}
                      >
                        <Entypo
                          name="dots-three-horizontal"
                          size={16}
                          color={
                            isMenuHovered
                              ? Colors.darkgrayColor
                              : Colors.grayColor
                          }
                        />
                      </View>
                    }
                    renderItem={({ item, close }) => (
                      <HoverableTouchableOpacity
                        style={styles.workoutMenuItemRow}
                        hoverStyle={styles.workoutMenuItemHover}
                        onPress={(e) => {
                          e?.stopPropagation?.();
                          if (item.key === "changeName") {
                            startEditingWorkoutName();
                          } else {
                            setShowingDeleteAlert(true);
                          }
                          close();
                        }}
                        title={item.label}
                      >
                        <Text
                          style={[
                            styles.workoutMenuItemText,
                            item.isDanger ? styles.workoutMenuItemDanger : null,
                          ]}
                        >
                          {item.label}
                        </Text>
                      </HoverableTouchableOpacity>
                    )}
                  />
                )}
              </View>

              <View style={styles.workoutRowBody}>
                {rows.length === 0 ? (
                  <Text style={styles.workoutRowEmptyText}>
                    {t("noExercisesYet-string")}
                  </Text>
                ) : (
                  rows.map((row) => (
                    <View key={row.id} style={styles.workoutPreviewRow}>
                      <Text numberOfLines={1} style={styles.workoutPreviewName}>
                        {row.name}
                      </Text>
                      <Text numberOfLines={1} style={styles.workoutPreviewSets}>
                        {row.setCount} {row.setsLabel}
                      </Text>
                    </View>
                  ))
                )}

                {remainingCount > 0 && (
                  <Text numberOfLines={1} style={styles.workoutPreviewOverflow}>
                    +{remainingCount}{" "}
                    {(remainingCount === 1
                      ? t("exercise-string")
                      : t("exercises-string")
                    ).toLowerCase()}
                  </Text>
                )}
              </View>
            </View>
          </TouchableOpacity>
          {showingDeleteAlert && (
            <CustomAlert
              isVisible={showingDeleteAlert}
              onClose={() => setShowingDeleteAlert(false)}
              title={deleteAlertTitle}
              text={deleteAlertText}
              action={() => deleteLibraryWorkout(workout)}
              specialCTA={"delete-string"}
            />
          )}
        </View>
      );
    },
  );

  return (
    <View
      style={[
        styles.container,
        { marginTop: screenSize === "small" ? 16 : 24 },
      ]}
    >
      <Observer>
        {() => {
          const filteredWorkouts = coach.getFilteredWorkoutLibrary();
          const orderedWorkoutIds = buildOrderedIds(filteredWorkouts);
          const canReorderWorkouts =
            !selectedWorkout &&
            (coach.searchTermWorkoutLibrary?.trim?.() ?? "") === "" &&
            filteredWorkouts.length > 1;

          return (
            <>
              <View
                style={[
                  GlobalStyles.HStack,
                  { marginBottom: 16, maxWidth: "45%" },
                ]}
              >
                <SearchBar
                  text={coach.searchTermWorkoutLibrary}
                  onChangeText={(text) =>
                    coach.setProperty("searchTermWorkoutLibrary", text)
                  }
                  placeholder={t("searchWorkout-string")}
                  autoFocusOnDesktop={true}
                />

                <CTAButton
                  plus={true}
                  text={t("newWorkout-string")}
                  action={() => setShowingAddNewWorkout(true)}
                  disabled={false}
                />
              </View>

              {coach.showWorkoutLibraryInfoView && (
                <TutorialLinkSection
                  text="tutorialLibraryWorkouts-string"
                  urlPath="/library/workouts"
                  marginTop={8}
                  marginBottom={8}
                />
              )}

              {filteredWorkouts.length === 0 ? (
                <IconEmptyView
                  iconLibrary={"Ionicons"}
                  mainText={"emptyWorkoutsTitle-string"}
                  subText={"createTemplateWorkoutsText-string"}
                  iconName={"folder-outline"}
                  marginTop={130}
                />
              ) : (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    flexWrap: "wrap",
                    gap: screenSize === "small" ? 8 : 12,
                    marginTop: screenSize === "small" ? 0 : 8,
                  }}
                >
                  {filteredWorkouts.map((workout, index) => (
                    <WorkoutRow
                      workout={workout}
                      key={workout.id}
                      dragIndex={index}
                      orderedIds={orderedWorkoutIds}
                      canReorder={canReorderWorkouts}
                      suppressPress={
                        pressSuppressedWorkoutId === String(workout?.id ?? "")
                      }
                      isMobile={isMobile}
                    />
                  ))}
                </View>
              )}

              {showingAddNewWorkout && (
                <AddWorkoutView
                  isVisible={showingAddNewWorkout}
                  onClose={() => setShowingAddNewWorkout(false)}
                  coach={coach}
                />
              )}

              {selectedWorkout && libraryBlockAdapter && (
                <Modal
                  animationType="fade"
                  transparent={true}
                  visible={true}
                  onRequestClose={handleWorkoutEditorModalRequestClose}
                >
                  <View
                    ref={handleLibraryFloatingPortalHostRef}
                    collapsable={false}
                    dataSet={{ efortcoachFloatingRoot: "true" }}
                    style={styles.editorModalBackdrop}
                  >
                    <TouchableOpacity
                      style={styles.editorModalBackdropTouch}
                      onPress={closeWorkoutEditorModal}
                      accessible={false}
                      focusable={false}
                      importantForAccessibility="no-hide-descendants"
                    />

                    <View
                      style={[
                        styles.editorModalCardShell,
                        { width: libraryWorkoutCardWidth },
                      ]}
                    >
                      <View
                        style={[
                          styles.editorWorkoutCardHost,
                          { width: libraryWorkoutCardWidth },
                        ]}
                      >
                        <ExerciseDragProvider>
                          <ExerciseDndProvider
                            onMoveExercise={handleMoveExercise}
                            onMoveExerciseCrossWorkout={() => null}
                          >
                            <WorkoutCard
                              workout={selectedWorkout}
                              block={libraryBlockAdapter}
                              coach={coach}
                              t={t}
                              formatWorkoutDate={() => null}
                              handleDuplicateWorkout={() => null}
                              exitWorkoutEditMode={closeWorkoutEditorModal}
                              persistBlockChange={() =>
                                persistLibraryWorkoutChange(
                                  coach,
                                  selectedWorkout,
                                )
                              }
                              handleCopyExercise={handleCopyExercise}
                              handlePasteExercise={handlePasteExercise}
                              handleDeleteWorkout={deleteLibraryWorkout}
                              mode="library"
                              showExerciseHistory={false}
                              showSetPriority={showSetPriority}
                              floatingSearchPortalRoot={
                                libraryFloatingPortalRoot
                              }
                              scrollExercisesInternally={true}
                              showLibraryCloseButton={true}
                            />
                          </ExerciseDndProvider>
                        </ExerciseDragProvider>
                      </View>
                    </View>
                  </View>
                </Modal>
              )}
            </>
          );
        }}
      </Observer>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.backgroundColor,
  },
  workoutRowTouchable: {
    borderRadius: 12,
    overflow: "hidden",
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    boxShadow: "none",
  },
  workoutRowTouchableDragging: {
    opacity: 0,
  },
  workoutRowCard: {
    height: 148,
    borderRadius: 12,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    overflow: "hidden",
  },
  workoutRowCardHovered: {
    borderColor: Colors.grayColor,
  },
  workoutRowCardDropZoneActive: {
    opacity: 0.55,
  },
  workoutRowHeader: {
    height: 44,
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    borderBottomWidth: 0.1,
    borderBottomColor: Colors.borderColor,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: Colors.whiteColor,
  },
  workoutRowTitle: {
    ...Fonts.medium14,
    flex: 1,
    minWidth: 0,
    color: Colors.blackColor,
  },
  workoutRowTitleInput: {
    ...Fonts.medium14,
    flex: 1,
    minWidth: 0,
    color: Colors.blackColor,
    padding: 0,
    outlineStyle: "none",
  },
  workoutRowTitleInputMobile: {
    fontSize: 16,
  },
  workoutInlineRenameActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  workoutInlineRenameButton: {
    width: 24,
    height: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  workoutInlineRenameButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  workoutRowMenuAnchor: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  workoutMenuItemRow: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  workoutMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  workoutMenuItemText: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  workoutMenuItemDanger: {
    color: Colors.redColor,
  },
  workoutRowBody: {
    flex: 1,
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 10,
    gap: 6,
  },
  workoutPreviewRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  workoutPreviewName: {
    flex: 1,
    minWidth: 0,
    fontSize: 12,
    lineHeight: 16,
    color: Colors.darkgrayColor,
  },
  workoutPreviewSets: {
    fontSize: 11,
    lineHeight: 16,
    color: Colors.grayColor,
    fontWeight: "500",
    flexShrink: 0,
  },
  workoutPreviewOverflow: {
    fontSize: 11,
    lineHeight: 16,
    color: Colors.grayColor,
    fontStyle: "italic",
    marginTop: 2,
  },
  workoutRowEmptyText: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.grayColor,
  },
  editorModalBackdrop: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(15, 15, 15, 0.2)",
  },
  editorModalBackdropTouch: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
  },
  editorModalCardShell: {
    maxHeight: "96%",
    alignSelf: "center",
    zIndex: 2,
    overflow: "hidden",
    justifyContent: "center",
  },
  editorWorkoutCardHost: {
    maxHeight: "100%",
    minHeight: 0,
  },
});

export default WorkoutLibrary;
